import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, type SxProps, Typography } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { DaysInWeek, DaysInWeekSelector } from "@/components/RecurringShift/util/DaysInWeekSelector";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import { type LocationEditorDesign, smallLabelTypographySx, V1_DAYS_PICKER_GAP, V1_SMALL_NUMERIC_FIELD_WIDTH } from "./consts";
import type { DayAmountRow } from "./locationConfigData";
import { StaffingTargetRowsV2 } from "./StaffingTargetRowsV2";

const smallNumericFieldSx: SxProps = {
	width: V1_SMALL_NUMERIC_FIELD_WIDTH,
} as const satisfies SxProps;

export type StaffingTargetRowsCommonProps = {
	rows: DayAmountRow[];
	/** Per row id: the short day names another row already holds. */
	disabledDaysByRow: Record<string, string[]>;
	/** Per row id: whether a recurring shift sits on one of its days, which makes it undeletable. */
	rowHasOccupiedDays: Record<string, boolean>;
	weekStartDay: number;
	onUpdateRow: (rowId: string, updates: Partial<DayAmountRow>) => void;
	onRemoveRow: (rowId: string) => void;
	onAddRow: () => void;
};

/** v2 alone has a read-only state: the Locations page's editor under a Schedule Template edit lock. */
export type StaffingTargetRowsProps = StaffingTargetRowsCommonProps &
	({ design: Extract<LocationEditorDesign, "v1"> } | { design: Extract<LocationEditorDesign, "v2">; disabled: boolean });

/** A PPD/HPPD location's staffing targets: day groups, each with its own Target and Required. */
export const StaffingTargetRows: FC<StaffingTargetRowsProps> = (props): ReactElement => {
	const { rows, disabledDaysByRow, rowHasOccupiedDays, weekStartDay, onUpdateRow, onRemoveRow, onAddRow } = props;
	const { t } = useTranslation();
	switch (props.design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						width: "100%",
					}}
				>
					<Typography sx={smallLabelTypographySx}>{t("schedule.template.staffing.targets")}</Typography>
					{rows.map((row, index) => {
						// DEFAULT: a row missing from the map has no days taken by other rows (moved verbatim).
						const disabledShortDays = disabledDaysByRow[row.id] ?? [];
						const disabledDaysInWeek = Object.values(DaysInWeek).filter((fullDay) =>
							disabledShortDays.some((short) => fullDay.toLowerCase().startsWith(short)),
						);
						const isLastRow = index === rows.length - 1;
						return (
							<Box
								key={row.id}
								data-testid={`location-staffing-target-row-${row.id}`}
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
								}}
							>
								<DaysInWeekSelector
									size="small"
									gap={V1_DAYS_PICKER_GAP}
									selectedDays={Object.values(DaysInWeek).filter((fullDay) => row.days.some((short) => fullDay.toLowerCase().startsWith(short)))}
									disabledDays={disabledDaysInWeek}
									disabledTooltipText={t("schedule.template.day.already.selected")}
									onChange={(selectedDays) =>
										onUpdateRow(row.id, {
											days: selectedDays.map((d) => d.toLowerCase().slice(0, 3)),
										})
									}
									weekStartDay={weekStartDay}
								/>
								<SystemValidationTextField
									label={t("schedule.template.target")}
									inputType={"number"}
									value={row.amount.toString()}
									onChange={(event) =>
										onUpdateRow(row.id, {
											// DEFAULT: a cleared or non-numeric field counts as 0 staff (moved verbatim).
											amount: Number.parseInt(event.target.value, 10) || 0,
										})
									}
									inputProps={{ inputProps: { min: 0 } }}
									inputSx={smallNumericFieldSx}
									reserveHeightForHelperText={false}
								/>
								<SystemValidationTextField
									label={t("schedule.template.required")}
									inputType={"number"}
									value={row.amountRequired.toString()}
									onChange={(event) =>
										onUpdateRow(row.id, {
											// DEFAULT: a cleared or non-numeric field counts as 0 staff (moved verbatim).
											amountRequired: Number.parseInt(event.target.value, 10) || 0,
										})
									}
									inputProps={{ inputProps: { min: 0, max: row.amount } }}
									inputSx={smallNumericFieldSx}
									reserveHeightForHelperText={false}
								/>
								<InHouseTooltip
									title={rowHasOccupiedDays[row.id] ? t("schedule.template.cannot.delete.target.with.shifts") : ""}
									disableHoverListener={!rowHasOccupiedDays[row.id]}
								>
									<span>
										<IconButton
											size="small"
											aria-label={t("schedule.template.v3.editor.removeRow")}
											data-testid={`location-staffing-target-remove-button-${row.id}`}
											onClick={(e) => {
												e.stopPropagation();
												onRemoveRow(row.id);
											}}
											disabled={rows.length <= 1 || rowHasOccupiedDays[row.id]}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</span>
								</InHouseTooltip>
								{isLastRow && (
									<IconButton
										size="small"
										aria-label={t("schedule.template.v3.editor.addRow")}
										data-testid="location-staffing-target-add-button"
										onClick={(e) => {
											e.stopPropagation();
											onAddRow();
										}}
									>
										<AddIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
						);
					})}
				</Box>
			);
		case "v2":
			return (
				<StaffingTargetRowsV2
					rows={rows}
					disabledDaysByRow={disabledDaysByRow}
					rowHasOccupiedDays={rowHasOccupiedDays}
					weekStartDay={weekStartDay}
					onUpdateRow={onUpdateRow}
					onRemoveRow={onRemoveRow}
					onAddRow={onAddRow}
					disabled={props.disabled}
				/>
			);
	}
};
